import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DEFAULT_INVITATION_EMAIL_TEMPLATE_HTML, DEFAULT_EMAIL_PLACEHOLDER_VALUES, EMAIL_HEADING_ROW_HTML, EMAIL_BODY_ROW_HTML, EMAIL_EVENT_DETAILS_HEADING_ROW_HTML, EMAIL_EVENT_DETAILS_SUBTEXT_ROW_HTML, EMAIL_BADGE_ROW_HTML } from './templates/english/invitationEmailDefaultEng';
import { Icon } from '@iconify/react';
import ButtonDropdown from '@ui/components/ButtonDropdown';
import { stripEditorParagraphTags, flattenEmailSingleLineParagraphs } from '@application/utilities/email-editor-typography';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getEmailTemplateByContextAsync } from '@application/features/emailTemplates/emailTemplatesThunk';
// Default Email Company Logo to be shown
const DEFAULT_EMAIL_COMPANY_LOGO = '';
/**
 * Converts <p> block elements to <span style="display:block"> so the content can be
 * safely placed inside an <h1> without causing invalid HTML (browsers close the <h1>
 * when they encounter a <p> child, which breaks all inherited styling).
 */
function pTagsToBlockSpans(html) {
    if (!html)
        return html;
    return html
        .replace(/<p(\s[^>]*)?>/gi, (_, attrs = '') => {
        const styleMatch = attrs.match(/style="([^"]*)"/i);
        const existingStyle = styleMatch ? styleMatch[1].replace(/;?\s*$/, '; ') : '';
        const otherAttrs = attrs.replace(/\s*style="[^"]*"/i, '').trim();
        return `<span style="${existingStyle}display:block"${otherAttrs ? ' ' + otherAttrs : ''}>`;
    })
        .replace(/<\/p>/gi, '</span>');
}
/**
 * Substitutes {{key}} in html with values from placeholderValues (or defaults), then renders in iframe.
 * Runs multiple passes so nested placeholders (e.g. {{event.title}} inside {{emailHeading}}) are resolved.
 */
function substitutePlaceholders(html, values) {
    const maxPasses = 5;
    let result = html;
    for (let pass = 0; pass < maxPasses; pass++) {
        const next = result.replace(/\{\{([^}]+)\}\}/g, (_, key) => {
            const k = key.trim();
            return values[k] ?? `{{${k}}}`;
        });
        if (next === result)
            break;
        result = next;
    }
    return result;
}
const EmailPreview = ({ htmlTemplate = DEFAULT_INVITATION_EMAIL_TEMPLATE_HTML, placeholderValues = {}, emailHeadingHtml, emailBodyHtml, emailEventDetailsHeadingHtml, emailEventDetailsSubtextHtml, emailBadgeHtml, emailButtonTextHtml, companyLogoPlaceholder = {} }) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const [selectedLang, setSelectedLang] = useState(i18n.language || 'en');
    const { languages, eventLanguages } = useAppSelector((state) => state.languages);
    const { selectedEmailTemplate } = useAppSelector((state) => state.emailTemplates);
    const dispatch = useAppDispatch();
    const onLanguageChange = (langCode) => {
        setSelectedLang(langCode);
        const language = languages?.find((lang) => lang.code.toLowerCase() === langCode.toLowerCase());
        if (!language || !selectedEmailTemplate)
            return;
        dispatch(getEmailTemplateByContextAsync({
            eventId: selectedEmailTemplate.eventId ?? '',
            languageId: language.id,
            templateType: selectedEmailTemplate.templateType
        }));
    };
    const flagIcon = (src) => (_jsx("img", { src: src, alt: '', style: { width: 20, height: 20, objectFit: 'contain', display: 'block' } }));
    const FLAG_CONFIG = {
        en: { src: '/assets/images/flags/us-flag.svg', text: t('language.english') },
        nl: { src: '/assets/images/flags/nl-flag.svg', text: t('language.dutch') },
        de: { src: '/assets/images/flags/de-flag.svg', text: t('language.german') }
    };
    const ALL_LANGUAGE_OPTIONS = [
        { id: 'en', text: t('language.english'), icon: flagIcon('/assets/images/flags/us-flag.svg'), group: true },
        { id: 'nl', text: t('language.dutch'), icon: flagIcon('/assets/images/flags/nl-flag.svg'), group: true },
        { id: 'de', text: t('language.german'), icon: flagIcon('/assets/images/flags/de-flag.svg'), group: true }
    ];
    const languageOptions = useMemo(() => {
        if (!eventLanguages || eventLanguages.length === 0)
            return ALL_LANGUAGE_OPTIONS;
        const eventCodes = new Set(eventLanguages.map((l) => l.code.toLowerCase()));
        return ALL_LANGUAGE_OPTIONS.filter((opt) => eventCodes.has(opt.id));
    }, [eventLanguages]);
    useEffect(() => {
        if (languageOptions.length > 0 && !languageOptions.some((opt) => opt.id === selectedLang)) {
            setSelectedLang(languageOptions[0].id);
        }
    }, [languageOptions]);
    const mergedValues = useMemo(() => {
        const base = {
            ...DEFAULT_EMAIL_PLACEHOLDER_VALUES,
            ...placeholderValues
        };
        if (emailHeadingHtml != null && emailHeadingHtml !== '') {
            // <p> inside <h1> is invalid HTML — browsers close the <h1> on the <p> open tag,
            // stripping all inherited h1 styles. Convert to block spans to keep valid markup.
            base.emailHeading = pTagsToBlockSpans(emailHeadingHtml);
        }
        if (emailBodyHtml != null && emailBodyHtml !== '') {
            base.emailBody = emailBodyHtml;
        }
        if (emailEventDetailsHeadingHtml != null && emailEventDetailsHeadingHtml !== '') {
            base.emailEventDetailsHeading = emailEventDetailsHeadingHtml;
        }
        if (emailEventDetailsSubtextHtml != null && emailEventDetailsSubtextHtml !== '') {
            base.emailEventDetailsSubtext = emailEventDetailsSubtextHtml;
        }
        if (emailBadgeHtml != null && emailBadgeHtml !== '') {
            base.emailBadge = stripEditorParagraphTags(flattenEmailSingleLineParagraphs(emailBadgeHtml));
        }
        if (emailButtonTextHtml != null && emailButtonTextHtml !== '') {
            base.emailButtonText = stripEditorParagraphTags(flattenEmailSingleLineParagraphs(emailButtonTextHtml));
        }
        const badgeContent = (base.emailBadge ?? '').replace(/<[^>]+>/g, '').trim();
        base.emailBadgeRow =
            badgeContent === ''
                ? ''
                : EMAIL_BADGE_ROW_HTML.replace(/\{\{emailBadge\}\}/g, base.emailBadge ?? '');
        const buttonTextContent = (base.emailButtonText ?? '').replace(/<[^>]+>/g, '').trim();
        if (buttonTextContent === '') {
            base.Button_Block_START = '<!--';
            base.Button_Block_END = '-->';
        }
        else {
            base.Button_Block_START = '';
            base.Button_Block_END = '';
        }
        const headingContent = (base.emailHeading ?? '').replace(/<[^>]+>/g, '').trim();
        base.emailHeadingRow =
            headingContent === ''
                ? ''
                : EMAIL_HEADING_ROW_HTML.replace(/\{\{emailHeading\}\}/g, base.emailHeading ?? '');
        const bodyContent = (base.emailBody ?? '').replace(/<[^>]+>/g, '').trim();
        base.emailBodyRow =
            bodyContent === ''
                ? ''
                : EMAIL_BODY_ROW_HTML.replace(/\{\{emailBody\}\}/g, base.emailBody ?? '');
        const detailsHeadingContent = (base.emailEventDetailsHeading ?? '')
            .replace(/<[^>]+>/g, '')
            .trim();
        base.emailEventDetailsHeadingRow =
            detailsHeadingContent === ''
                ? ''
                : EMAIL_EVENT_DETAILS_HEADING_ROW_HTML.replace(/\{\{emailEventDetailsHeading\}\}/g, base.emailEventDetailsHeading ?? '');
        const detailsSubtextContent = (base.emailEventDetailsSubtext ?? '')
            .replace(/<[^>]+>/g, '')
            .trim();
        base.emailEventDetailsSubtextRow =
            detailsSubtextContent === ''
                ? ''
                : EMAIL_EVENT_DETAILS_SUBTEXT_ROW_HTML.replace(/\{\{emailEventDetailsSubtext\}\}/g, base.emailEventDetailsSubtext ?? '');
        return base;
    }, [
        placeholderValues,
        emailHeadingHtml,
        emailBodyHtml,
        emailEventDetailsHeadingHtml,
        emailEventDetailsSubtextHtml,
        emailBadgeHtml,
        emailButtonTextHtml
    ]);
    let substitutedHtml = useMemo(() => substitutePlaceholders(htmlTemplate, mergedValues), [htmlTemplate, mergedValues]);
    substitutedHtml = substitutedHtml.replace('{{branding.logoUrl}}', companyLogoPlaceholder['branding.logoUrl'] && companyLogoPlaceholder['branding.logoUrl'] !== ''
        ? companyLogoPlaceholder['branding.logoUrl']
        : DEFAULT_EMAIL_COMPANY_LOGO);
    const iframeRef = useRef(null);
    useLayoutEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe?.contentDocument)
            return;
        const doc = iframe.contentDocument;
        doc.open();
        // Inject CSS so paragraph spacing and font rendering inside the preview
        // matches the editor (which sets margin: 0 / margin-bottom: 0.65em on <p>).
        const previewCss = `<style>
      .email-body-content p, .email-body-content li,
      h1 p, h1 span[data-block] {
        margin: 0;
        margin-bottom: 0.65em;
      }
      .email-body-content p:last-child { margin-bottom: 0; }
    </style>`;
        doc.write(substitutedHtml.replace('</head>', previewCss + '</head>'));
        doc.close();
    }, [substitutedHtml]);
    return (_jsxs(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            gap: 2,
            overflow: 'hidden'
        }, children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', children: [_jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }, children: [_jsx(Icon, { icon: 'ci:mail', width: '24', height: '24', color: theme.palette.common.black }), _jsx(Typography, { variant: 'h4', children: t('events.eventDetails.preferencesTab.emailPreview') })] }), _jsx(ButtonDropdown, { variant: 'outlined', size: 'medium', color: 'primary', label: '', options: languageOptions, selectedItem: selectedLang, onSelectionChange: onLanguageChange, startIcon: flagIcon(FLAG_CONFIG[selectedLang]?.src ?? '/assets/images/flags/us-flag.svg'), displayWithIdForLabel: true })] }), _jsx(Box, { sx: {
                    flex: 1,
                    overflow: 'auto'
                }, children: _jsx("iframe", { ref: iframeRef, title: 'Email preview', sandbox: 'allow-same-origin', style: {
                        width: '100%',
                        height: 'calc(100dvh - 219px)',
                        border: 'none',
                        display: 'block'
                    } }) })] }));
};
export default EmailPreview;
